import React from "react";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Menu } from "@design-system/Menu/Menu.lib";

export type ConnectionButtonVariant =
    | "primary"
    | "secondary"
    | "tertiary"
    | "destructive"
    | "link"
    | "menuItem";

export type ConnectionButtonSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface ConnectionButtonAppearance {
    variant?: ConnectionButtonVariant;
    size?: ConnectionButtonSize;
    className?: string;
    disabled?: boolean;
    leftIcon?: React.ReactNode;
    tooltip?: React.ReactNode;
}

interface ConnectionActionButtonProps extends ConnectionButtonAppearance {
    loading: boolean;
    onClick?: (event: React.MouseEvent<HTMLElement>) => Promise<void> | void;
    onKeyPress?: React.KeyboardEventHandler<HTMLButtonElement>;
    tabIndex?: number;
    type?: "button" | "submit" | "reset";
    "data-testid"?: string;
    children: React.ReactNode;
}

export type SolidButtonVariant = Exclude<
    ConnectionButtonVariant,
    "link" | "menuItem"
>;
export type SolidButtonSize = Exclude<ConnectionButtonSize, "xs">;

/**
 * Distributes `variant` across Button's discriminated union so a single
 * solid-button return type-checks without a cast. The two arms are identical
 * at runtime; the split exists only so each matches a VariantProps member.
 */
export function solidVariantProps(
    variant: SolidButtonVariant,
    size: SolidButtonSize,
):
    | { variant: "primary" | "secondary"; size: SolidButtonSize }
    | { variant: "tertiary" | "destructive"; size: SolidButtonSize } {
    if (variant === "tertiary" || variant === "destructive") {
        return { variant, size };
    }
    return { variant, size };
}

export const ConnectionActionButton: React.FC<ConnectionActionButtonProps> = ({
    variant = "primary",
    size,
    className,
    disabled,
    leftIcon,
    tooltip,
    loading,
    onClick,
    onKeyPress,
    tabIndex,
    type,
    "data-testid": dataTestId,
    children,
}) => {
    const solidSize = size === "xs" || size === undefined ? "md" : size;
    const sharedProps = {
        className,
        disabled,
        leftIcon,
        loading,
        onClick,
        onKeyPress,
        tabIndex,
        type,
        "data-testid": dataTestId,
        children,
    };

    if (variant === "menuItem") {
        return (
            <Menu.Item
                closeOnClick={false}
                disabled={disabled || loading}
                onClick={onClick}
                tooltip={tooltip}
                data-testid={dataTestId}
            >
                {children}
            </Menu.Item>
        );
    }

    if (variant === "link") {
        // The design-system link variant only ships xs/sm; larger sizes clamp
        // to sm, mirroring the solid branch's xs→md clamp in the other
        // direction.
        return (
            <ButtonWithLoader
                {...sharedProps}
                variant="link"
                size={size === "xs" ? "xs" : "sm"}
            />
        );
    }

    return (
        <ButtonWithLoader
            {...sharedProps}
            {...solidVariantProps(variant, solidSize)}
        />
    );
};
